import { useQueries } from '@tanstack/react-query';
import { getVehicleScoreYears } from '../services/VehiclesService';
import { getDriverScoreYears } from '../services/DriversService';

export interface ComputedScores {
  score: number | null;
  scoreDaily: number | null;
  scoreWeekly: number | null;
  scoreMonthly: number | null;
}

type EntityType = 'vehicle' | 'driver';

function computeScores(
  years: { year: number; days: { date: string; score: number; drivingTimeMs: number }[] }[],
): ComputedScores {
  const now = new Date();
  const allDays = years.flatMap((y) => y.days);
  const drivingDays = allDays.filter((d) => (d.drivingTimeMs ?? 0) > 0 && new Date(d.date) <= now);

  const avg = (days: typeof drivingDays): number | null => {
    const totalDrivingTime = days.reduce((s, d) => s + (d.drivingTimeMs ?? 0), 0);
    if (totalDrivingTime <= 0) return null;
    const weightedSum = days.reduce((s, d) => s + d.score * (d.drivingTimeMs ?? 0), 0);
    return weightedSum / totalDrivingTime;
  };

  const dayOfWeek = now.getUTCDay();
  const weekStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - ((dayOfWeek + 6) % 7)));
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  return {
    score: avg(drivingDays),
    scoreDaily: avg(drivingDays.filter((d) => new Date(d.date) >= todayStart)),
    scoreWeekly: avg(drivingDays.filter((d) => new Date(d.date) >= weekStart)),
    scoreMonthly: avg(drivingDays.filter((d) => new Date(d.date) >= monthStart)),
  };
}

export function useEntityScores(
  fleetId: string,
  ids: string[],
  type: EntityType,
): { scores: Map<string, ComputedScores>; isLoading: boolean } {
  const currentYear = new Date().getFullYear();

  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: [`${type}-score-years-computed`, id, fleetId],
      queryFn: () => (type === 'vehicle'
        ? getVehicleScoreYears(id, fleetId, 2020, currentYear)
        : getDriverScoreYears(id, fleetId, 2020, currentYear)),
      retry: 1,
      retryDelay: 3000,
      staleTime: 60_000,
      enabled: !!id && !!fleetId,
    })),
  });

  const isLoading = ids.length > 0 && results.some((r) => r.isLoading || r.isFetching);

  const scores = new Map<string, ComputedScores>();
  results.forEach((r, i) => {
    if (r.data) {
      const years = (r.data as any).years ?? [];
      scores.set(ids[i], computeScores(years));
    }
  });
  return { scores, isLoading };
}
